/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { apiSlice } from "../apiSlice";
import { API, API_SLICE_TAGS } from "../../../globals/globals";
import { FetchBaseQueryMeta } from "@reduxjs/toolkit/dist/query";
import {
  ITaskArgs,
  ITaskLogData,
  ITemplates,
  ITaskDetails,
  ICustomAuditTemplate,
  ICustomAuditTemplateDetail,
  ITemplatePreviewResponse,
  ICustomAuditTemplatePreviewResponse,
} from "./types";
import { sanitizeQueryParams } from "../../helpers/helpers";
import { IArgs, IDeviceAction } from "../devices/types";

export const taskApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getScheduledTasks: builder.query<ITaskDetails[], IArgs>({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.SCHEDULEDTASKS.path}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.SCHEDULEDTASKS],
    }),
    getTemplates: builder.query<ITemplates[], IArgs>({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.TEMPLATES.path}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.TEMPLATES],
    }),
    getTemplateById: builder.query<any, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `${API.ENDPOINTS.TEMPLATES.path}/template?templateId=${encodeURIComponent(templateId)}`,
      }),
    }),
    getTemplatePreview: builder.query<ITemplatePreviewResponse, { templateId: string; page: number; size: number }>({
      query: ({ templateId, page, size }) => ({
        url: `${API.ENDPOINTS.TEMPLATES.path}/${encodeURIComponent(templateId)}/preview?page=${page}&size=${size}`,
      }),
    }),
    getCustomAuditTemplates: builder.query<ICustomAuditTemplate[], void>({
      query: () => ({
        url: `${API.ENDPOINTS.CUSTOM_AUDIT_TEMPLATES.path}`,
      }),
      providesTags: [API_SLICE_TAGS.CUSTOM_AUDIT_TEMPLATES],
    }),
    getCustomAuditTemplateById: builder.query<ICustomAuditTemplateDetail, { id: number }>({
      query: ({ id }) => ({
        url: `${API.ENDPOINTS.CUSTOM_AUDIT_TEMPLATES.path}/${id}`,
      }),
    }),
    getCustomAuditTemplatePreview: builder.query<ICustomAuditTemplatePreviewResponse, { id: number; page: number; size: number }>({
      query: ({ id, page, size }) => ({
        url: `${API.ENDPOINTS.CUSTOM_AUDIT_TEMPLATES.path}/${id}/preview?page=${page}&size=${size}`,
      }),
    }),
    getTaskLogs: builder.query<ITaskLogData[], ITaskArgs>({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.TASKLOGS.path}?taskName=${args?.taskName}`,
      }),
    }),
    executeTask: builder.mutation({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.RUNTASK.path}/${args?.taskName}`,
        method: "POST",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    deleteTask: builder.mutation({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.DELETETASK.path}?taskId=${args?.taskName}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getExportTemplate: builder.mutation({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.EXPORTTEMP.path}?template=${args?.template}`,
        method: "GET",
        headers: {
          "Content-Type": "application/xml",
        },
      }),
    }),
    exportCustomAuditTemplate: builder.mutation({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.CUSTOM_AUDIT_TEMPLATES_EXPORT.path}?template=${args?.template}`,
        method: "GET",
        headers: {
          "Content-Type": "application/xml",
        },
      }),
    }),
    createTask: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.CREATETASK.path}`,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateTask: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.UPDATETASK.path}/${payload.name}`,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    importTemplate: builder.mutation<any, { formData: FormData; fileType: string; deviceKey: string; description?: string }>({
      query: ({ formData, fileType, deviceKey, description = "" }) => ({
        url: `${API.ENDPOINTS.IMPORTTEMPLATE.path}/${fileType}?deviceKey=${encodeURIComponent(deviceKey)}&description=${encodeURIComponent(description)}`,
        method: "POST",
        body: formData,
        responseHandler: "text",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    uploadCustomAuditTemplate: builder.mutation<ICustomAuditTemplate, { formData: FormData; fileType: string; description?: string }>({
      query: ({ formData, fileType, description = "" }) => ({
        url: `${API.ENDPOINTS.CUSTOM_AUDIT_TEMPLATES.path}?fileType=${encodeURIComponent(fileType)}&description=${encodeURIComponent(description)}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [API_SLICE_TAGS.CUSTOM_AUDIT_TEMPLATES],
    }),
    runOnDemandTask: builder.mutation({
      query: (payload: IDeviceAction) => ({
        url: `${API.ENDPOINTS.RUNONDEMANDTASK.path}/${payload.deviceKey}/${payload.taskName}`,
        method: "PUT",
        body: { ...payload.taskProperties },
        responseHandler: (response: any) => response.text(),
      }),
      // transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
      //   return { status: meta?.response?.status, ...data };
      // },
    }),
    applyTemplate: builder.mutation({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.APPLYTEMPLATE.path}/${args?.fileType}/${args?.deviceKey}?templateName=${args?.templateName}`,
        method: "POST",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getAllTaskLogs: builder.query({
      query: () => ({
        url: `${API.ENDPOINTS.ALLTASKLOGS.path}`,
      }),
    }),
    deleteTemplate: builder.mutation({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.TEMPLATES.path}/delete?templateId=${args?.template}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteCustomAuditTemplate: builder.mutation<{ message: string }, { id: number }>({
      query: ({ id }) => ({
        url: `${API.ENDPOINTS.CUSTOM_AUDIT_TEMPLATES.path}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [API_SLICE_TAGS.CUSTOM_AUDIT_TEMPLATES],
    }),
    getAllTemplateLogs: builder.query({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.TEMPLATELOGS.path}/${args?.deviceId}`,
      }),
    }),
    getTaskLogDetails: builder.query({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.TASKLOGDETAILS.path}/${args.taskName}/${args?.taskExecutionKey}`,
      }),
    }),
    getReportList: builder.query<Array<string>, void>({
      query: () => ({
        url: `${API.ENDPOINTS.REPORTLIST.path}`,
      }),
    }),
    getReportTaskList: builder.query<Array<string>, string>({
      query: (geoRegion) => ({
		url: `${API.ENDPOINTS.REPORTTASKLIST.path}?geoRegion=${encodeURIComponent(geoRegion)}`,
      }),
    }),
  }),
});

export const {
  useGetScheduledTasksQuery,
  useGetTaskLogsQuery,
  useGetTemplatesQuery,
  useGetTemplateByIdQuery,
  useGetTemplatePreviewQuery,
  useGetCustomAuditTemplatesQuery,
  useGetCustomAuditTemplateByIdQuery,
  useGetCustomAuditTemplatePreviewQuery,
  useExecuteTaskMutation,
  useDeleteTaskMutation,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useImportTemplateMutation,
  useUploadCustomAuditTemplateMutation,
  useGetExportTemplateMutation,
  useExportCustomAuditTemplateMutation,
  useRunOnDemandTaskMutation,
  useApplyTemplateMutation,
  useGetAllTaskLogsQuery,
  useDeleteTemplateMutation,
  useDeleteCustomAuditTemplateMutation,
  useGetAllTemplateLogsQuery,
  useGetTaskLogDetailsQuery,
  useGetReportListQuery,
  useGetReportTaskListQuery,
} = taskApiSlice;
